import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary para capturar errores de React causados por extensiones del navegador
 * que modifican el DOM (Grammarly, Google Translate, correctores ortográficos, etc.)
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Actualizar el estado para que el siguiente renderizado muestre la UI alternativa
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Registrar el error para debugging
    console.error('Error Boundary capturó un error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Detectar si es causado por extensiones del navegador
    const isExtensionError = 
      error.message?.includes('insertBefore') ||
      error.message?.includes('removeChild') ||
      error.message?.includes('Node') ||
      error.name === 'NotFoundError';

    if (isExtensionError) {
      console.warn(
        '⚠️ Error causado probablemente por extensiones del navegador (Grammarly, Translate, etc.)\n' +
        'Recomendación: Desactivar extensiones en esta página o refrescar.'
      );
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      const isExtensionError = 
        this.state.error?.message?.includes('insertBefore') ||
        this.state.error?.message?.includes('removeChild') ||
        this.state.error?.name === 'NotFoundError';

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-12 w-12 text-red-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Algo salió mal
                </h1>
                <p className="text-gray-600 mt-1">
                  La aplicación encontró un error inesperado
                </p>
              </div>
            </div>

            {isExtensionError && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-yellow-900 mb-2">
                  🔍 Posible causa: Extensiones del navegador
                </h3>
                <p className="text-sm text-yellow-800 mb-3">
                  Este error suele ocurrir cuando extensiones como <strong>Grammarly</strong>, 
                  <strong> Google Translate</strong> o <strong>correctores ortográficos</strong> modifican 
                  la página automáticamente.
                </p>
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold mb-1">Soluciones recomendadas:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Refresca la página haciendo clic en el botón de abajo</li>
                    <li>Desactiva temporalmente las extensiones del navegador</li>
                    <li>Usa el modo incógnito del navegador</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="flex gap-3 mb-6">
              <button
                onClick={this.handleReload}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <RefreshCw className="h-5 w-5" />
                Refrescar página
              </button>
              
              <button
                onClick={this.handleReset}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Intentar continuar
              </button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                  Detalles técnicos (solo en desarrollo)
                </summary>
                <div className="mt-3 p-4 bg-gray-100 rounded text-xs font-mono overflow-auto max-h-60">
                  <div className="text-red-600 font-semibold mb-2">
                    {this.state.error.name}: {this.state.error.message}
                  </div>
                  <div className="text-gray-700 whitespace-pre-wrap">
                    {this.state.error.stack}
                  </div>
                  {this.state.errorInfo && (
                    <div className="mt-4 pt-4 border-t border-gray-300">
                      <div className="font-semibold text-gray-800 mb-2">Component Stack:</div>
                      <div className="text-gray-600">
                        {this.state.errorInfo.componentStack}
                      </div>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
